import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with base configuration
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('evolutt_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const status = error.response.status;

      if (status === 401) {
        // Token expired or invalid → clear stored auth and force re-login.
        // Feb 2026: previously we only logged a warning and let each component
        // show its own toast, but that led to a confusing "Token expirado (HTTP 401)"
        // stuck on screen with no way forward. Now the whole app hard-resets to /login.
        try {
          localStorage.removeItem('evolutt_token');
          localStorage.removeItem('evolutt_user');
        } catch (_) {}
        // Avoid infinite redirects when the failed request IS the login itself.
        const url = (error.config && error.config.url) || '';
        const path = window.location.pathname || '';
        if (!url.includes('/auth/login') && !path.startsWith('/login')) {
          window.location.assign('/login?expired=1');
        }
      } else if (status === 403) {
        console.warn('Access forbidden (403)');
      }
    } else if (error.request) {
      console.warn('Network error - server may be unavailable');
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (email, password, name) => api.post('/auth/register', { email, password, name }),
  getMe: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => 
    api.post('/user/change-password', { current_password: currentPassword, new_password: newPassword }),
};

// CV API
export const cvAPI = {
  list: (params = {}) => api.get('/cvs', { params }),
  get: (id) => api.get(`/cvs/${id}`),
  create: (data) => api.post('/cvs', data),
  update: (id, data) => api.put(`/cvs/${id}`, data),
  delete: (id) => api.delete(`/cvs/${id}`),
  exportWord: (id, lang) => api.get(`/cvs/${id}/export/${lang}`, { responseType: 'blob' }),
  preview: (id, lang) => api.get(`/cvs/${id}/preview/${lang}`, { responseType: 'blob' }),
};

// Competencies API
export const competenciesAPI = {
  list: (category = null) => api.get('/competencies', { params: category ? { category } : {} }),
  getCategories: () => api.get('/competencies/categories'),
  create: (data) => api.post('/competencies', data),
  update: (id, data) => api.put(`/competencies/${id}`, data),
  delete: (id) => api.delete(`/competencies/${id}`),
};

// Clients API
export const clientsAPI = {
  list: () => api.get('/clients'),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Availability Options API
export const availabilityAPI = {
  list: () => api.get('/availability-options'),
  create: (data) => api.post('/availability-options', data),
  update: (id, data) => api.put(`/availability-options/${id}`, data),
  delete: (id) => api.delete(`/availability-options/${id}`),
};

// IT Talents API
export const itTalentsAPI = {
  list: () => api.get('/it-talents'),
  create: (data) => api.post('/it-talents', data),
  delete: (id) => api.delete(`/it-talents/${id}`),
};

// Recruiters API
export const recruitersAPI = {
  list: () => api.get('/recruiters'),
  create: (data) => api.post('/recruiters', data),
  delete: (id) => api.delete(`/recruiters/${id}`),
};

// AI API
export const aiAPI = {
  generateSummary: (data) => api.post('/ai/generate-summary', data),
  importCV: (data) => api.post('/ai/import-cv', data),
  generateScoreObservation: (data) => api.post('/ai/score-observation', data),
};

export default api;
